import type { BreadcrumbItem } from '@hub/common/Breadcrumb/Breadcrumb';
import { Breadcrumb } from '@hub/common/Breadcrumb/Breadcrumb';
import { ContactBox } from '@hub/common/ContactBox/ContactBox';
import { Header } from '@hub/layout/Header/Header';
import { ScrollToTopButton } from '@hub/layout/ScrollToTopButton/ScrollToTopButton';
import type { NodeContact } from '@hub/models/api/node-contact.types';
import { LocaleSelectionContextProvider } from '@hub/hooks/useLocaleSelectionContext';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { fetchClientHeaderData } from '@hub/libs/storyblok/storyblok.api.client';
import { StoryblokFooter } from '@hub/layout/Footer/Footer';
import type { LayoutData } from '@hub/page-helpers/getInitialLayoutData';
import type { HeaderProps } from '@hub/layout/Header/types';

export interface BaseLayoutProps {
  breadcrumbItems?: BreadcrumbItem[];
  contact?: NodeContact | null;
  layout: LayoutData;
  children?: React.ReactNode;
}

export const BaseLayout = ({ breadcrumbItems, contact, layout, children }: BaseLayoutProps) => {
  const router = useRouter();
  // POP is parameter coming from the following story to make header, footer etc. dissappear
  // https://igusdev.atlassian.net/jira/software/c/projects/COM/boards/255?modal=detail&selectedIssue=COM-5734&sprint=1804
  const isPop = router.query?.POP === 'yes';

  const [clientHeader, setClientHeader] = useState<HeaderProps>(layout.header);

  // fetch detailed header data ONCE on client side
  useEffect(() => {
    void (async () => {
      const header = await fetchClientHeaderData(router.locale);

      if (!header) {
        return;
      }

      const nextHeader = {
        ...layout.header,
        header,
      };

      setClientHeader(nextHeader);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LocaleSelectionContextProvider>
      {!isPop && (
        <Header
          header={clientHeader.header}
          default_form_slug={clientHeader.default_form_slug}
          languageAlternates={clientHeader.languageAlternates}
          translations={clientHeader.translations}
        />
      )}
      <nav className="container my-5 text-base font-normal text-gray-500">
        {breadcrumbItems && !isPop && <Breadcrumb items={breadcrumbItems} addHome />}
      </nav>
      <main className="block container my-8 text-base font-normal text-gray-500 bg-white">{children}</main>
      {contact && !isPop && (
        <section className="my-8 text-base font-normal text-gray-500">
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <ContactBox {...contact} contactStandardTexts={layout.contacts} />
        </section>
      )}
      {!isPop && <StoryblokFooter footer={layout.footer} />}
      <ScrollToTopButton />
    </LocaleSelectionContextProvider>
  );
};
